import React, {useCallback, useEffect, useState} from "react";
import {Card} from "reactstrap";

import AppointmentsService from "services/appointments";
import {toUkDisplayDate} from "helpers/ukDate";

const NOT_FOUND = 404;
const NO_ACCESS = 400;

/**
 * The bands, mirrored from the backend's QuestionnaireScoring so this file can
 * render one without a second round trip.
 *
 * THE BACKEND IS THE SOURCE OF TRUTH and it sends the band with the score. This
 * is a fallback for a response that predates that field, nothing more - if the
 * two ever disagree, the API wins, because it is the one the clinical record is
 * written from.
 */
const PHQ9_BANDS = [
    [0, 4, "minimal"],
    [5, 9, "mild"],
    [10, 14, "moderate"],
    [15, 19, "moderately severe"],
    [20, 27, "severe"],
];

const GAD7_BANDS = [
    [0, 4, "minimal"],
    [5, 9, "mild"],
    [10, 14, "moderate"],
    [15, 21, "severe"],
];

export function bandFor(bands, total) {
    if (typeof total !== "number" || Number.isNaN(total)) {
        return null;
    }
    const match = bands.find(([min, max]) => total >= min && total <= max);

    return match ? match[2] : null;
}

/**
 * How to describe one instrument, given whatever the API sent.
 *
 * Returns null when there is no score at all, so the caller can tell "not
 * completed" from "completed, scoring zero". Those are different facts and a
 * screen that renders them alike turns silence into a reassuring number.
 */
export function describeScore(total, apiBand, bands) {
    if (typeof total !== "number") {
        return null;
    }

    return { total, band: apiBand || bandFor(bands, total) };
}

/**
 * PHQ-9 and GAD-7 for one consultation, as a clinician reads them.
 *
 * Read-only, deliberately. These are the PATIENT's own answers; a clinician
 * editing them would turn a self-report into something else still wearing its
 * name. Corrections belong to the patient re-answering.
 *
 * ITEM 9 IS REPORTED, NOT ALERTED ON. It is shown as what it is - one of the
 * nine PHQ-9 items, and a clinically relevant one for the consultation about to
 * happen - rather than as a red banner demanding action here.
 *
 * That is a deliberate decision, made by Sunil 2026-09-23: the CRISIS PATHWAY
 * LIVES IN THE PATIENT APP, which signposts 999, Samaritans, NHS 111 and SHOUT
 * at the moment the patient answers, and lets them carry on to their
 * appointment afterwards. The response therefore does not depend on anybody
 * opening this screen in time, which is exactly what an alert here would have
 * implied and could not have delivered.
 *
 * NO SCORES, NO SECTION (Sunil, 2026-09-24). Not completed, still loading, or no
 * access all render nothing - there is no "not completed" line. That also hides
 * it on every GP consultation without the admin needing the service type: the
 * API refuses answers on a GP booking (403), so a GP slot can never have any and
 * always reads as 404. A GENUINE failure still shows, so a broken request is
 * never mistaken for a patient who did not answer.
 */
export function QuestionnaireScoresSection({ appointment }) {
    const [scores, setScores] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await AppointmentsService.getQuestionnaires(appointment.id);
            setScores(data);
            setError("");
        } catch (e) {
            // The shared axios interceptor rejects with the parsed response BODY, not the axios
            // error, so there is no `e.response` to read a status from. The body carries the HTTP
            // status on `code`, which is what the interceptor itself keys on.
            const status = e?.code || e?.statusCode;

            setScores(null);
            setError(status === NOT_FOUND || status === NO_ACCESS
                ? ""
                : "Could not load the questionnaire scores.");
        }
    }, [appointment.id]);

    useEffect(() => { load(); }, [load]);

    if (!scores && !error) {
        return null;
    }

    const phq9 = scores ? describeScore(scores.phq9Total, scores.phq9Band, PHQ9_BANDS) : null;
    const gad7 = scores ? describeScore(scores.gad7Total, scores.gad7Band, GAD7_BANDS) : null;

    return (
        <Card className="p-3 mb-3">
            <h5 className="mb-3">Mental health questionnaires</h5>

            {error ? <div className="text-danger mb-2">{error}</div> : null}

            {scores ? (
                <>
                    <div className="mb-2">
                        <strong>PHQ-9 (depression):</strong>{" "}
                        {phq9 ? (
                            <span data-testid="phq9-score">
                                {phq9.total} / 27{phq9.band ? ` — ${phq9.band}` : ""}
                            </span>
                        ) : "—"}
                    </div>

                    {/* Reported plainly, for the clinician reading the score before the
                        consultation. Any answer above zero is shown - a patient can endorse
                        item 9 and still total in the mild band, so the total alone would not
                        say so - but it is a line of the record, not an alarm: the crisis
                        signposting the patient actually needs already happened in the app. */}
                    {typeof scores.phq9Question9 === "number" ? (
                        <div className="mb-2 text-muted" data-testid="phq9-item9">
                            Item 9 (thoughts of self-harm): {scores.phq9Question9} of 3
                        </div>
                    ) : null}

                    <div className="mb-2">
                        <strong>GAD-7 (anxiety):</strong>{" "}
                        {gad7 ? (
                            <span data-testid="gad7-score">
                                {gad7.total} / 21{gad7.band ? ` — ${gad7.band}` : ""}
                            </span>
                        ) : "—"}
                    </div>

                    <div className="text-muted">
                        Completed {scores.completedAt ? toUkDisplayDate(scores.completedAt) : "—"}
                    </div>
                </>
            ) : null}
        </Card>
    );
}
